import { useState, useRef, useEffect, useCallback, useMemo } from "react";

const TRIMMER_API = "https://d9gp4dwpjw4in.cloudfront.net";

// ── Cognito Config ──────────────────────────────────────────────────────────
const COGNITO = {
  region:   "us-east-2",
  poolId:   "us-east-2_behl3sU6w",
  clientId: "2vfe8cmjtrh3ro424mlkp1e9nt",
  endpoint: "https://cognito-idp.us-east-2.amazonaws.com/",
};

async function cognitoAuth(username, password) {
  const res = await fetch(COGNITO.endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-amz-json-1.1", "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth" },
    body: JSON.stringify({ AuthFlow: "USER_PASSWORD_AUTH", ClientId: COGNITO.clientId, AuthParameters: { USERNAME: username, PASSWORD: password } }),
  });
  return res.json();
}

async function cognitoNewPassword(username, newPassword, session, name) {
  const res = await fetch(COGNITO.endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-amz-json-1.1", "X-Amz-Target": "AWSCognitoIdentityProviderService.RespondToAuthChallenge" },
    body: JSON.stringify({
      ChallengeName: "NEW_PASSWORD_REQUIRED", ClientId: COGNITO.clientId,
      ChallengeResponses: { USERNAME: username, NEW_PASSWORD: newPassword, "userAttributes.website": "https://trimmer.starstandard.org", "userAttributes.name": name || username },
      Session: session,
    }),
  });
  return res.json();
}

async function cognitoForgotPassword(username) {
  const res = await fetch(COGNITO.endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-amz-json-1.1", "X-Amz-Target": "AWSCognitoIdentityProviderService.ForgotPassword" },
    body: JSON.stringify({ ClientId: COGNITO.clientId, Username: username }),
  });
  return res.json();
}

async function cognitoConfirmForgotPassword(username, code, newPassword) {
  const res = await fetch(COGNITO.endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-amz-json-1.1", "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmForgotPassword" },
    body: JSON.stringify({ ClientId: COGNITO.clientId, Username: username, ConfirmationCode: code, Password: newPassword }),
  });
  return res.json();
}

// ── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [stage, setStage] = useState("login");
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [resetCode, setResetCode] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [panelContent, setPanelContent] = useState(null);

  useEffect(() => {
    fetch(`https://star-standard-content.s3.amazonaws.com/trimmer-panel.json?t=${Date.now()}`)
      .then(r => r.json()).then(setPanelContent).catch(() => setPanelContent(null));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault(); setError(null); setLoading(true);
    try {
      const data = await cognitoAuth(email, password);
      if (data.ChallengeName === "NEW_PASSWORD_REQUIRED") { setSession(data.Session); setStage("newpassword"); }
      else if (data.AuthenticationResult) { const tok = data.AuthenticationResult.IdToken; localStorage.setItem("star_token", tok); localStorage.setItem("star_email", email); onLogin(tok, email); }
      else setError(data.message || data.__type || "Login failed");
    } catch { setError("Network error — please try again"); } finally { setLoading(false); }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault(); setError(null);
    if (newPassword !== confirmPassword) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const data = await cognitoNewPassword(email, newPassword, session, fullName);
      if (data.AuthenticationResult) { const tok = data.AuthenticationResult.IdToken; localStorage.setItem("star_token", tok); localStorage.setItem("star_email", email); onLogin(tok, email); }
      else setError(data.message || data.__type || "Failed to set password");
    } catch { setError("Network error"); } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setError(null); setLoading(true);
    try {
      const data = await cognitoForgotPassword(email);
      if (data.__type) setError(data.message || data.__type);
      else { setSuccessMsg(`Reset code sent to ${data.CodeDeliveryDetails?.Destination || email}`); setStage("confirm"); }
    } catch { setError("Network error"); } finally { setLoading(false); }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault(); setError(null);
    if (resetPassword !== resetConfirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const data = await cognitoConfirmForgotPassword(email, resetCode, resetPassword);
      if (data.__type) setError(data.message || data.__type);
      else { setSuccessMsg("Password reset! Please sign in."); setStage("login"); }
    } catch { setError("Network error"); } finally { setLoading(false); }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=JetBrains+Mono:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Syne', sans-serif; }
        .login-wrap { min-height: 100vh; display: grid; grid-template-columns: 460px 1fr; }
        .login-left { background: #ffffff; padding: 56px 48px; display: flex; flex-direction: column; justify-content: center; min-height: 100vh; }
        .login-logo-text { font-size: 22px; font-weight: 800; font-style: italic; letter-spacing: -.02em; margin-bottom: 4px; }
        .login-logo-text span:first-child { color: #f59e0b; }
        .login-logo-text span:last-child { color: #242438; }
        .login-logo-sub { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: .16em; text-transform: uppercase; color: #9b98b0; margin-bottom: 40px; }
        .login-title { font-size: 24px; font-weight: 700; color: #242438; margin-bottom: 6px; }
        .login-subtitle { font-size: 14px; color: #6b6880; margin-bottom: 28px; line-height: 1.5; }
        .login-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: .12em; text-transform: uppercase; color: #6b6880; display: block; margin-bottom: 6px; }
        .login-input { width: 100%; background: #f8f7ff; border: 1.5px solid #e8e5f5; border-radius: 8px; padding: 12px 14px; color: #242438; font-family: 'Syne', sans-serif; font-size: 14px; outline: none; transition: border-color .2s; margin-bottom: 16px; }
        .login-input:focus { border-color: #f59e0b; }
        .login-input::placeholder { color: #c4c0d8; }
        .login-btn { width: 100%; padding: 13px; border-radius: 8px; border: none; background: #f59e0b; color: #fff; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 15px; cursor: pointer; margin-top: 4px; transition: opacity .2s; }
        .login-btn:hover { opacity: .88; } .login-btn:disabled { opacity: .4; cursor: not-allowed; }
        .login-error { background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 10px 14px; color: #dc2626; font-size: 12px; margin-bottom: 16px; font-family: 'JetBrains Mono', monospace; }
        .login-success { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 10px 14px; color: #16a34a; font-size: 12px; margin-bottom: 16px; font-family: 'JetBrains Mono', monospace; }
        .login-divider { border: none; border-top: 1px solid #f0eeff; margin: 20px 0; }
        .login-link { background: none; border: none; color: #f59e0b; font-family: 'JetBrains Mono', monospace; font-size: 11px; cursor: pointer; text-decoration: underline; display: block; text-align: center; }
        .login-hint { font-size: 12px; color: #9b98b0; text-align: center; margin-top: 20px; font-family: 'JetBrains Mono', monospace; line-height: 1.6; }
        .login-right { background: #1c1c2e; padding: 56px 48px; display: flex; flex-direction: column; justify-content: center; position: relative; overflow: hidden; }
        .login-right::before { content: ''; position: absolute; top: -100px; right: -100px; width: 400px; height: 400px; background: radial-gradient(circle, rgba(201,168,76,.15) 0%, transparent 70%); pointer-events: none; }
        .login-right::after { content: ''; position: absolute; bottom: -80px; left: -60px; width: 300px; height: 300px; background: radial-gradient(circle, rgba(20,184,166,.1) 0%, transparent 70%); pointer-events: none; }
        .panel-hero { margin-bottom: 32px; position: relative; z-index: 1; }
        .panel-headline { font-size: 26px; font-weight: 800; color: #f0eeff; margin-bottom: 8px; line-height: 1.2; }
        .panel-tagline { font-size: 14px; color: #b8b5cc; line-height: 1.6; margin-bottom: 12px; }
        .panel-badge { display: inline-block; font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .08em; color: #f59e0b; background: rgba(201,168,76,.12); border: 1px solid rgba(201,168,76,.2); padding: 4px 12px; border-radius: 20px; }
        .panel-section { margin-bottom: 22px; position: relative; z-index: 1; }
        .panel-section-title { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: #f59e0b; margin-bottom: 10px; }
        .panel-divider { border: none; border-top: 1px solid #363660; margin: 0 0 12px; }
        .panel-item { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 7px; font-size: 13px; color: #c8c5dc; line-height: 1.5; }
        .panel-item::before { content: '·'; color: #f59e0b; flex-shrink: 0; font-weight: 700; }
        .panel-link { display: flex; align-items: center; gap: 6px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #b8b5cc; text-decoration: none; margin-bottom: 6px; transition: color .15s; }
        .panel-link:hover { color: #f59e0b; }
        .panel-link::before { content: '→'; color: #f59e0b; font-weight: 700; }
        .skeleton { background: #363660; border-radius: 4px; animation: pulse 1.5s infinite; }
        @keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:.8} }
        @media (max-width: 768px) { .login-wrap { grid-template-columns: 1fr; } .login-right { display: none; } }
      `}</style>
      <div className="login-wrap">
        <div className="login-left">
          <div>
            <div className="login-logo-text"><span>STAR</span><span> API Trimmer</span></div>
            <div className="login-logo-sub">API Subsetter Tool</div>
          </div>

          {stage === "login" && (<>
            <div className="login-title">Welcome back</div>
            <div className="login-subtitle">Sign in to trim your STAR API specs to minimal subsets.</div>
            {error && <div className="login-error">{error}</div>}
            {successMsg && <div className="login-success">{successMsg}</div>}
            <form onSubmit={handleLogin}>
              <label className="login-label">Email</label>
              <input className="login-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@starstandard.org" required />
              <label className="login-label">Password</label>
              <input className="login-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
              <button className="login-btn" type="submit" disabled={loading}>{loading ? "Signing in…" : "Sign In →"}</button>
            </form>
            <hr className="login-divider" />
            <button className="login-link" onClick={() => { setStage("forgot"); setError(null); setSuccessMsg(null); }}>Forgot password?</button>
            <div className="login-hint">Access is provided by your STAR administrator.<br/>Contact your admin if you need an account.</div>
          </>)}

          {stage === "newpassword" && (<>
            <div className="login-title">Set your password</div>
            <div className="login-subtitle">First sign in — set a permanent password.</div>
            {error && <div className="login-error">{error}</div>}
            <form onSubmit={handleNewPassword}>
              <label className="login-label">Your Name</label>
              <input className="login-input" type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="First Last" required />
              <label className="login-label">New Password</label>
              <input className="login-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 chars, upper+lower+number+symbol" required />
              <label className="login-label">Confirm Password</label>
              <input className="login-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" required />
              <button className="login-btn" type="submit" disabled={loading}>{loading ? "Setting…" : "Set Password & Sign In →"}</button>
            </form>
          </>)}

          {stage === "forgot" && (<>
            <div className="login-title">Reset password</div>
            <div className="login-subtitle">Enter your email and we'll send a reset code.</div>
            {error && <div className="login-error">{error}</div>}
            <form onSubmit={handleForgotPassword}>
              <label className="login-label">Email</label>
              <input className="login-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@starstandard.org" required />
              <button className="login-btn" type="submit" disabled={loading}>{loading ? "Sending…" : "Send Reset Code →"}</button>
            </form>
            <hr className="login-divider" />
            <button className="login-link" onClick={() => { setStage("login"); setError(null); }}>← Back to sign in</button>
          </>)}

          {stage === "confirm" && (<>
            <div className="login-title">Enter reset code</div>
            <div className="login-subtitle">{successMsg || "Check your email for the reset code."}</div>
            {error && <div className="login-error">{error}</div>}
            <form onSubmit={handleConfirmReset}>
              <label className="login-label">Reset Code</label>
              <input className="login-input" type="text" value={resetCode} onChange={e => setResetCode(e.target.value)} placeholder="6-digit code" required />
              <label className="login-label">New Password</label>
              <input className="login-input" type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} placeholder="New password" required />
              <label className="login-label">Confirm Password</label>
              <input className="login-input" type="password" value={resetConfirm} onChange={e => setResetConfirm(e.target.value)} placeholder="Repeat password" required />
              <button className="login-btn" type="submit" disabled={loading}>{loading ? "Resetting…" : "Reset Password →"}</button>
            </form>
          </>)}
        </div>

        <div className="login-right">
          {!panelContent ? (
            <>
              <div style={{marginBottom:32}}>
                <div className="skeleton" style={{height:28,width:"70%",marginBottom:10}} />
                <div className="skeleton" style={{height:14,width:"90%",marginBottom:6}} />
                <div className="skeleton" style={{height:14,width:"55%",marginBottom:12}} />
                <div className="skeleton" style={{height:22,width:"60%",borderRadius:20}} />
              </div>
              {[1,2,3].map(i => (
                <div key={i} style={{marginBottom:22}}>
                  <div className="skeleton" style={{height:10,width:"40%",marginBottom:10}} />
                  <div style={{borderTop:"1px solid #363660",marginBottom:10}} />
                  <div className="skeleton" style={{height:10,width:"88%",marginBottom:6}} />
                  <div className="skeleton" style={{height:10,width:"72%",marginBottom:6}} />
                  <div className="skeleton" style={{height:10,width:"80%"}} />
                </div>
              ))}
            </>
          ) : (<>
            <div className="panel-hero">
              <div className="panel-headline">{panelContent.hero?.headline}</div>
              <div className="panel-tagline">{panelContent.hero?.tagline}</div>
              {panelContent.hero?.badge && <div className="panel-badge">{panelContent.hero.badge}</div>}
            </div>
            {(panelContent.sections || []).map((section, i) => (
              <div key={i} className="panel-section">
                <div className="panel-section-title">{section.title}</div>
                <div className="panel-divider" />
                {(section.items || []).map((item, j) => (
                  <div key={j} className="panel-item">{item}</div>
                ))}
                {(section.links || []).map((link, j) => (
                  <a key={j} href={link.url} className="panel-link" target="_blank" rel="noopener noreferrer">{link.label}</a>
                ))}
              </div>
            ))}
          </>)}
        </div>
      </div>
    </>
  );
}

// ── Method Badge ──────────────────────────────────────────────────────────────
const METHOD_COLORS = {
  GET:    { bg: "rgba(96,165,250,.12)",  color: "#60a5fa" },
  POST:   { bg: "rgba(16,185,129,.12)",  color: "#10b981" },
  PUT:    { bg: "rgba(245,158,11,.12)",  color: "#f59e0b" },
  PATCH:  { bg: "rgba(167,139,250,.12)", color: "#e4c97e" },
  DELETE: { bg: "rgba(239,68,68,.12)",   color: "#ef4444" },
};

function MethodBadge({ method }) {
  const c = METHOD_COLORS[method] || { bg: "rgba(156,163,175,.12)", color: "#9ca3af" };
  return (
    <span style={{ background: c.bg, color: c.color, fontFamily: "var(--mono)",
      fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
      letterSpacing: ".08em", flexShrink: 0 }}>
      {method}
    </span>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("star_token"));
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem("star_email") || "");

  // Step state: upload | select | result
  const [step, setStep] = useState("upload");
  const [content, setContent] = useState("");
  const [fileName, setFileName] = useState("");
  const [specInfo, setSpecInfo] = useState(null);  // { operations, use_cases, title, version }
  const [loading, setLoading] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedOps, setSelectedOps] = useState(new Set());
  const [filterMethod, setFilterMethod] = useState("ALL");
  const [filterSearch, setFilterSearch] = useState("");

  // Result state
  const [trimResult, setTrimResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const authHeaders = useMemo(() => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${token}`
  }), [token]);

  const handleLogin = (tok, email) => { setToken(tok); setUserEmail(email); };
  const handleLogout = useCallback(() => {
    localStorage.removeItem("star_token"); localStorage.removeItem("star_email");
    setToken(null); setUserEmail("");
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => setContent(e.target.result);
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const loadSpec = async () => {
    if (!content.trim()) { setError("Please upload or paste a spec first"); return; }
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${TRIMMER_API}/trim/list`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ content }),
      });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      if (data.detail) { setError(data.detail); return; }
      setSpecInfo(data);
      setSelectedOps(new Set());
      setStep("select");
    } catch (e) {
      setError(`Failed to load spec: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectUseCase = (uc) => {
    setSelectedOps(new Set(uc.operation_ids));
  };

  const toggleOp = (opKey) => {
    setSelectedOps(prev => {
      const next = new Set(prev);
      if (next.has(opKey)) next.delete(opKey);
      else next.add(opKey);
      return next;
    });
  };

  const selectAll = () => {
    if (!specInfo) return;
    setSelectedOps(new Set(specInfo.operations.map(op => `${op.method}:${op.path}`)));
  };

  const selectNone = () => setSelectedOps(new Set());

  const trimSpec = async () => {
    if (selectedOps.size === 0) { setError("Please select at least one operation"); return; }
    setTrimming(true); setError(null);
    try {
      const res = await fetch(`${TRIMMER_API}/trim`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ content, selected_operations: Array.from(selectedOps), run_validation: true }),
      });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      if (data.detail) { setError(data.detail); return; }
      setTrimResult(data);
      setStep("result");
    } catch (e) {
      setError(`Trim failed: ${e.message}`);
    } finally {
      setTrimming(false);
    }
  };

  const downloadTrimmed = () => {
    if (!trimResult?.trimmed_yaml) return;
    const blob = new Blob([trimResult.trimmed_yaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const base = fileName.replace(".yaml","").replace(".yml","");
    a.download = `${base}_trimmed.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStep("upload"); setContent(""); setFileName(""); setSpecInfo(null);
    setSelectedOps(new Set()); setTrimResult(null); setError(null);
  };

  // Filter operations
  const filteredOps = specInfo?.operations?.filter(op => {
    if (filterMethod !== "ALL" && op.method !== filterMethod) return false;
    if (filterSearch && !op.path.toLowerCase().includes(filterSearch.toLowerCase()) &&
        !op.summary.toLowerCase().includes(filterSearch.toLowerCase()) &&
        !op.operation_id.toLowerCase().includes(filterSearch.toLowerCase())) return false;
    return true;
  }) || [];

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=JetBrains+Mono:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --bg:      #1c1c2e; --bg2: #242438; --bg3: #2a2a42;
          --border:  #363660; --border2: #424268;
          --text:    #f0eeff; --muted: #c8c5e0; --dim: #9a98b5;
          --amber:   #f59e0b; --amber2: #d97706;
          --purple:  #c9a84c; --teal: #14b8a6; --green: #10b981;
          --red:     #ef4444;
          --sans: 'Syne', sans-serif; --mono: 'JetBrains Mono', monospace;
        }
        body { background: #1c1c2e; color: var(--text); font-family: var(--sans); font-size: 15px; line-height: 1.7; min-height: 100vh; }

        /* Topbar */
        .topbar { display: flex; align-items: center; gap: 20px; padding: 0 28px; height: 52px;
          background: #2a2d35; border-bottom: 1px solid #3a3d45; position: sticky; top: 0; z-index: 100; }
        .logo { font-size: 17px; font-weight: 800; font-style: italic; letter-spacing: -.02em; flex-shrink: 0; }
        .logo span:first-child { color: var(--amber); }
        .logo span:last-child { color: var(--text); }
        .topbar-badge { font-family: var(--mono); font-size: 10px; letter-spacing: .12em; text-transform: uppercase;
          color: var(--amber); background: rgba(245,158,11,.1); border: 1px solid rgba(245,158,11,.2);
          padding: 3px 8px; border-radius: 4px; }
        .topbar-right { margin-left: auto; display: flex; align-items: center; gap: 14px; }
        .topbar-email { font-family: var(--mono); font-size: 11px; color: var(--muted); }
        .signout-btn { font-family: var(--mono); font-size: 9px; letter-spacing: .1em; text-transform: uppercase;
          background: transparent; border: 1px solid #424268; border-radius: 4px;
          color: var(--dim); padding: 4px 10px; cursor: pointer; transition: all .2s; }
        .signout-btn:hover { border-color: var(--red); color: var(--red); }

        /* Steps indicator */
        .steps { display: flex; align-items: center; gap: 0; padding: 16px 28px;
          background: #242438; border-bottom: 1px solid #363660; }
        .step-item { display: flex; align-items: center; gap: 8px; }
        .step-num { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center;
          justify-content: center; font-family: var(--mono); font-size: 11px; font-weight: 700;
          border: 2px solid var(--border2); color: var(--dim); transition: all .2s; }
        .step-num.active { border-color: var(--amber); color: var(--amber); background: rgba(245,158,11,.1); }
        .step-num.done { border-color: var(--green); color: var(--green); background: rgba(16,185,129,.1); }
        .step-label { font-family: var(--mono); font-size: 10px; letter-spacing: .08em; text-transform: uppercase;
          color: var(--dim); transition: color .2s; }
        .step-label.active { color: var(--amber); }
        .step-label.done { color: var(--green); }
        .step-connector { width: 40px; height: 1px; background: var(--border2); margin: 0 8px; }

        /* Main layout */
        .main { max-width: 1100px; margin: 0 auto; padding: 32px 28px; }

        /* Upload step */
        .upload-card { background: #242438; border: 1px solid #363660; border-radius: 14px; padding: 32px; margin-bottom: 20px; }
        .upload-title { font-size: 22px; font-weight: 800; margin-bottom: 6px; }
        .upload-sub { font-family: var(--mono); font-size: 12px; color: var(--muted); margin-bottom: 24px; }
        .drop-zone { border: 2px dashed #424268; border-radius: 10px; padding: 48px 20px;
          text-align: center; cursor: pointer; transition: all .2s; background: #1c1c2e; }
        .drop-zone.drag-over { border-color: var(--amber); background: rgba(245,158,11,.05); }
        .drop-zone:hover { border-color: var(--border2); background: #2a2a42; }
        .drop-icon { font-size: 36px; margin-bottom: 12px; }
        .drop-text { font-size: 15px; color: var(--muted); margin-bottom: 6px; }
        .drop-hint { font-family: var(--mono); font-size: 10px; color: var(--dim); }
        .or-divider { text-align: center; font-family: var(--mono); font-size: 10px; color: var(--dim);
          margin: 16px 0; letter-spacing: .1em; }
        .paste-area { width: 100%; background: #1c1c2e; border: 1px solid #363660; border-radius: 8px;
          padding: 14px; color: #f0dfa0; font-family: var(--mono); font-size: 12px; line-height: 1.7;
          resize: vertical; min-height: 160px; outline: none; transition: border-color .2s; }
        .paste-area:focus { border-color: var(--amber); }
        .paste-area::placeholder { color: var(--dim); }
        .load-btn { width: 100%; padding: 14px; border-radius: 10px; border: none; margin-top: 16px;
          background: linear-gradient(135deg, var(--amber), var(--amber2));
          color: #fff; font-family: var(--sans); font-weight: 700; font-size: 16px;
          cursor: pointer; transition: opacity .2s; }
        .load-btn:hover { opacity: .88; } .load-btn:disabled { opacity: .4; cursor: not-allowed; }

        /* Select step */
        .select-header { display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 24px; gap: 20px; flex-wrap: wrap; }
        .spec-title { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
        .spec-meta { font-family: var(--mono); font-size: 11px; color: var(--muted); }

        /* Use cases */
        .use-cases { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; margin-bottom: 24px; }
        .use-case-card { background: #242438; border: 1px solid #363660; border-radius: 10px;
          padding: 14px 16px; cursor: pointer; transition: all .2s; }
        .use-case-card:hover { border-color: var(--amber); background: rgba(245,158,11,.05); }
        .use-case-icon { font-size: 20px; margin-bottom: 6px; }
        .use-case-label { font-weight: 700; font-size: 14px; margin-bottom: 4px; }
        .use-case-desc { font-family: var(--mono); font-size: 10px; color: var(--muted); line-height: 1.5; }

        /* Operations filter */
        .ops-filter { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; align-items: center; }
        .filter-btn { padding: 6px 14px; border-radius: 6px; border: 1px solid #424268;
          background: transparent; color: var(--muted); font-family: var(--mono); font-size: 10px;
          letter-spacing: .06em; cursor: pointer; transition: all .2s; }
        .filter-btn.active { border-color: var(--amber); color: var(--amber); background: rgba(245,158,11,.1); }
        .filter-search { flex: 1; min-width: 160px; background: #242438; border: 1px solid #424268;
          border-radius: 6px; padding: 7px 12px; color: var(--text); font-family: var(--mono); font-size: 11px;
          outline: none; transition: border-color .2s; }
        .filter-search:focus { border-color: var(--amber); }
        .filter-search::placeholder { color: var(--dim); }
        .sel-actions { display: flex; gap: 8px; margin-left: auto; }
        .sel-btn { padding: 6px 12px; border-radius: 6px; border: 1px solid #424268;
          background: transparent; color: var(--dim); font-family: var(--mono); font-size: 10px; cursor: pointer; }
        .sel-btn:hover { color: var(--muted); border-color: var(--border2); }

        /* Operations list */
        .ops-list { background: #242438; border: 1px solid #363660; border-radius: 10px; overflow: hidden; margin-bottom: 20px; }
        .op-row { display: flex; align-items: center; gap: 12px; padding: 11px 16px;
          border-bottom: 1px solid #363660; cursor: pointer; transition: background .15s; }
        .op-row:last-child { border-bottom: none; }
        .op-row:hover { background: rgba(245,158,11,.04); }
        .op-row.selected { background: rgba(245,158,11,.07); }
        .op-check { width: 16px; height: 16px; accent-color: var(--amber); cursor: pointer; flex-shrink: 0; }
        .op-path { font-family: var(--mono); font-size: 12px; color: var(--teal); flex: 1; }
        .op-summary { font-size: 12px; color: var(--muted); flex: 1; }
        .op-id { font-family: var(--mono); font-size: 10px; color: var(--dim); }

        /* Trim button */
        .trim-bar { display: flex; align-items: center; gap: 16px; background: #242438;
          border: 1px solid #363660; border-radius: 10px; padding: 16px 20px; }
        .trim-count { font-family: var(--mono); font-size: 12px; color: var(--muted); }
        .trim-count strong { color: var(--amber); }
        .trim-btn { padding: 12px 28px; border-radius: 8px; border: none; margin-left: auto;
          background: linear-gradient(135deg, var(--amber), var(--amber2));
          color: #fff; font-family: var(--sans); font-weight: 700; font-size: 15px;
          cursor: pointer; transition: opacity .2s; }
        .trim-btn:hover { opacity: .88; } .trim-btn:disabled { opacity: .4; cursor: not-allowed; }

        /* Result step */
        .result-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
        .stat-card { background: #242438; border: 1px solid #363660; border-radius: 10px; padding: 18px 20px; }
        .stat-label { font-family: var(--mono); font-size: 10px; letter-spacing: .1em; text-transform: uppercase;
          color: var(--muted); margin-bottom: 10px; }
        .stat-row { display: flex; justify-content: space-between; align-items: center;
          padding: 6px 0; border-bottom: 1px solid #363660; font-size: 13px; }
        .stat-row:last-child { border-bottom: none; }
        .stat-before { color: var(--dim); font-family: var(--mono); font-size: 12px; }
        .stat-after { color: var(--amber); font-family: var(--mono); font-size: 12px; font-weight: 700; }
        .stat-reduction { color: var(--green); font-family: var(--mono); font-size: 10px; }
        .download-btn { width: 100%; padding: 14px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, var(--green), #059669);
          color: #fff; font-family: var(--sans); font-weight: 700; font-size: 16px;
          cursor: pointer; transition: opacity .2s; margin-bottom: 12px; }
        .download-btn:hover { opacity: .88; }
        .restart-btn { width: 100%; padding: 12px; border-radius: 8px; border: 1px solid #424268;
          background: transparent; color: var(--muted); font-family: var(--mono); font-size: 11px;
          cursor: pointer; transition: all .2s; }
        .restart-btn:hover { border-color: var(--amber); color: var(--amber); }
        .validation-banner { border-radius: 10px; padding: 14px 18px; margin-bottom: 16px;
          display: flex; align-items: center; gap: 12px; font-size: 14px; font-weight: 700; }
        .validation-banner.valid { background: rgba(16,185,129,.1); border: 1px solid rgba(16,185,129,.3); color: var(--green); }
        .validation-banner.invalid { background: rgba(239,68,68,.1); border: 1px solid rgba(239,68,68,.3); color: var(--red); }
        .error-box { background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.3); border-radius: 10px;
          padding: 14px 18px; color: var(--red); font-family: var(--mono); font-size: 12px; margin-bottom: 16px; }
      `}</style>

      {/* Topbar */}
      <div className="topbar">
        <div className="logo"><span>STAR</span><span> API Trimmer</span></div>
        <div className="topbar-badge">API Subsetter</div>
        <div className="topbar-right">
          <span className="topbar-email">{userEmail}</span>
          <button className="signout-btn" onClick={handleLogout}>Sign out</button>
        </div>
      </div>

      {/* Steps */}
      <div className="steps">
        {[
          { id: "upload", num: 1, label: "Upload Spec" },
          { id: "select", num: 2, label: "Select Operations" },
          { id: "result", num: 3, label: "Download Subset" },
        ].map((s, i) => {
          const isDone = (s.id === "upload" && (step === "select" || step === "result")) ||
                         (s.id === "select" && step === "result");
          const isActive = step === s.id;
          return (
            <div key={s.id} style={{display:"flex",alignItems:"center"}}>
              {i > 0 && <div className="step-connector" />}
              <div className="step-item">
                <div className={`step-num ${isActive ? "active" : isDone ? "done" : ""}`}>
                  {isDone ? "✓" : s.num}
                </div>
                <div className={`step-label ${isActive ? "active" : isDone ? "done" : ""}`}>{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="main">
        {error && <div className="error-box">❌ {error}</div>}

        {/* ── STEP 1: UPLOAD ── */}
        {step === "upload" && (
          <div className="upload-card">
            <div className="upload-title">Upload your STAR API spec</div>
            <div className="upload-sub">Drop a monolith YAML file or paste content — we'll show you all operations to choose from</div>

            <div
              className={`drop-zone ${dragOver ? "drag-over" : ""}`}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".yaml,.yml,.json" style={{display:"none"}}
                onChange={e => handleFile(e.target.files[0])} />
              <div className="drop-icon">{fileName ? "✅" : "📂"}</div>
              <div className="drop-text">{fileName ? fileName : "Drop file here or click to browse"}</div>
              <div className="drop-hint">{fileName ? `${content.split('\n').length} lines loaded` : ".yaml · .yml · .json"}</div>
            </div>

            <div className="or-divider">— OR PASTE CONTENT —</div>

            <textarea className="paste-area" value={content} onChange={e => setContent(e.target.value)}
              placeholder={"openapi: '3.0.3'\ninfo:\n  title: Repair Order API\n  version: 1.0.0\npaths:\n  /repair-orders:\n    ..."} />

            <button className="load-btn" onClick={loadSpec} disabled={loading || !content.trim()}>
              {loading ? "Loading spec…" : "Load Spec & Select Operations →"}
            </button>
          </div>
        )}

        {/* ── STEP 2: SELECT ── */}
        {step === "select" && specInfo && (
          <div>
            <div className="select-header">
              <div>
                <div className="spec-title">{specInfo.title || "API Spec"}</div>
                <div className="spec-meta">
                  v{specInfo.version} · {specInfo.total_operations} operations · {specInfo.total_schemas} schemas
                </div>
              </div>
              <button onClick={() => setStep("upload")} style={{fontFamily:"var(--mono)",fontSize:10,
                background:"transparent",border:"1px solid var(--border2)",borderRadius:6,
                color:"var(--dim)",padding:"6px 12px",cursor:"pointer"}}>
                ← Change file
              </button>
            </div>

            {/* Use case shortcuts */}
            {specInfo.use_cases?.length > 0 && (
              <div style={{marginBottom:24}}>
                <div style={{fontFamily:"var(--mono)",fontSize:10,letterSpacing:".1em",
                  textTransform:"uppercase",color:"var(--muted)",marginBottom:12}}>
                  Quick select by use case
                </div>
                <div className="use-cases">
                  {specInfo.use_cases.map(uc => (
                    <div key={uc.id} className="use-case-card" onClick={() => selectUseCase(uc)}>
                      <div className="use-case-icon">{uc.icon}</div>
                      <div className="use-case-label">{uc.label}</div>
                      <div className="use-case-desc">{uc.description}</div>
                      <div style={{marginTop:8,fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)"}}>
                        {uc.operation_ids.length} operations
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filter bar */}
            <div className="ops-filter">
              {["ALL","GET","POST","PUT","PATCH","DELETE"].map(m => (
                <button key={m} className={`filter-btn ${filterMethod === m ? "active" : ""}`}
                  onClick={() => setFilterMethod(m)}>{m}</button>
              ))}
              <input className="filter-search" value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)} placeholder="Search operations…" />
              <div className="sel-actions">
                <button className="sel-btn" onClick={selectAll}>All</button>
                <button className="sel-btn" onClick={selectNone}>None</button>
              </div>
            </div>

            {/* Operations list */}
            <div className="ops-list">
              {filteredOps.length === 0 && (
                <div style={{padding:24,textAlign:"center",fontFamily:"var(--mono)",fontSize:12,color:"var(--dim)"}}>
                  No operations match filter
                </div>
              )}
              {filteredOps.map((op, i) => {
                const key = `${op.method}:${op.path}`;
                const isSelected = selectedOps.has(key);
                return (
                  <div key={i} className={`op-row ${isSelected ? "selected" : ""}`} onClick={() => toggleOp(key)}>
                    <input type="checkbox" className="op-check" checked={isSelected} onChange={() => toggleOp(key)} onClick={e => e.stopPropagation()} />
                    <MethodBadge method={op.method} />
                    <span className="op-path">{op.path}</span>
                    <span className="op-summary">{op.summary}</span>
                    <span className="op-id">{op.operation_id}</span>
                  </div>
                );
              })}
            </div>

            {/* Trim bar */}
            <div className="trim-bar">
              <div className="trim-count">
                <strong>{selectedOps.size}</strong> of {specInfo.total_operations} operations selected
                {selectedOps.size > 0 && <span style={{color:"var(--muted)",marginLeft:8}}>— schemas will be resolved automatically</span>}
              </div>
              <button className="trim-btn" onClick={trimSpec} disabled={trimming || selectedOps.size === 0}>
                {trimming ? "Trimming…" : "✂️ Trim Spec →"}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: RESULT ── */}
        {step === "result" && trimResult && (
          <div>
            <div style={{fontSize:24,fontWeight:800,marginBottom:20,color:"var(--amber)"}}>
              ✂️ Spec trimmed successfully!
            </div>

            {/* Validation banner */}
            {trimResult.validation && (
              <div className={`validation-banner ${trimResult.validation.summary?.is_valid ? "valid" : "invalid"}`}>
                <span style={{fontSize:20}}>{trimResult.validation.summary?.is_valid ? "✅" : "⚠️"}</span>
                <div>
                  <div>{trimResult.validation.summary?.is_valid
                    ? "Trimmed spec is STAR compliant"
                    : `${trimResult.validation.summary?.errors} validation issues in trimmed spec`}
                  </div>
                  <div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:400,color:"var(--muted)",marginTop:2}}>
                    {trimResult.validation.summary?.passed} checks passed · {trimResult.validation.summary?.warnings} warnings
                  </div>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="result-grid">
              <div className="stat-card">
                <div className="stat-label">Size Reduction</div>
                {[
                  ["Operations", trimResult.stats.original.operations, trimResult.stats.trimmed.operations, trimResult.stats.reduction.operations_pct],
                  ["Schemas", trimResult.stats.original.schemas, trimResult.stats.trimmed.schemas, trimResult.stats.reduction.schemas_pct],
                  ["Lines", trimResult.stats.original.size_lines, trimResult.stats.trimmed.size_lines, trimResult.stats.reduction.size_pct],
                ].map(([label, before, after, pct]) => (
                  <div key={label} className="stat-row">
                    <span>{label}</span>
                    <span className="stat-before">{before?.toLocaleString()}</span>
                    <span>→</span>
                    <span className="stat-after">{after?.toLocaleString()}</span>
                    <span className="stat-reduction">-{pct}%</span>
                  </div>
                ))}
              </div>

              <div className="stat-card">
                <div className="stat-label">Selected Operations</div>
                <div style={{maxHeight:140,overflowY:"auto"}}>
                  {Array.from(selectedOps).map((op, i) => {
                    const [method, ...pathParts] = op.split(":");
                    const path = pathParts.join(":");
                    return (
                      <div key={i} style={{display:"flex",gap:8,alignItems:"center",padding:"5px 0",
                        borderBottom:"1px solid var(--border)"}}>
                        <MethodBadge method={method} />
                        <span style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--teal)"}}>{path}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Actions */}
            <button className="download-btn" onClick={downloadTrimmed}>
              ⬇ Download Trimmed YAML
            </button>
            <button className="restart-btn" onClick={() => { setStep("select"); setTrimResult(null); }}>
              ← Back to selection
            </button>
            <button className="restart-btn" style={{marginTop:8}} onClick={reset}>
              Start over with new file
            </button>
          </div>
        )}
      </div>
    </>
  );
}
